/**
 * Welcome/Onboarding page - Introduces core concepts
 */

import { GlobalHeader } from './GlobalHeader';
import './WelcomePage.css';

interface WelcomePageProps {
  onGetStarted: () => void;
}

export function WelcomePage({ onGetStarted }: WelcomePageProps) {
  return (
    <div className="welcome-page">
      <GlobalHeader title="> WELCOME">
        <button className="get-started-btn" onClick={onGetStarted}>
          GET STARTED →
        </button>
      </GlobalHeader>

      <div className="welcome-content">
        <div className="welcome-section intro">
          <h2>&gt; SYSTEM OVERVIEW</h2>
          <p>
            This platform enables multi-modal interaction between users and AI entities
            in shared 3D environments. Learn the core concepts below to get started.
          </p>
        </div>

        <div className="concepts-grid">
          <div className="concept-card">
            <h3>&gt; SIMULATIONS</h3>
            <div className="concept-description">
              <p>
                Interactive 3D environments where users and AI entities coexist and interact
                through multiple modalities.
              </p>
              <ul>
                <li>Spatial audio communication</li>
                <li>3D movement and positioning</li>
                <li>Real-time interaction</li>
                <li>Extensible modality support</li>
              </ul>
            </div>
          </div>

          <div className="concept-card">
            <h3>&gt; TOPOLOGIES</h3>
            <div className="concept-description">
              <p>
                Cortical structures that define an AI entity's neural architecture -
                the blueprint for how it processes information and behaves.
              </p>
              <ul>
                <li>Neural network configurations</li>
                <li>Information processing pathways</li>
                <li>Behavioral patterns</li>
                <li>Reusable across simulations</li>
              </ul>
            </div>
          </div>

          <div className="concept-card">
            <h3>&gt; AI MODELS</h3>
            <div className="concept-description">
              <p>
                AI entities configured with specific topologies that can automatically
                spawn and participate in simulations.
              </p>
              <ul>
                <li>Assigned cortical topology</li>
                <li>Auto-spawn capability</li>
                <li>Configurable positioning</li>
                <li>Interactive participants</li>
              </ul>
            </div>
          </div>

          <div className="concept-card">
            <h3>&gt; ENTITIES</h3>
            <div className="concept-description">
              <p>
                All participants within a simulation - both human users and AI models -
                positioned in 3D space.
              </p>
              <ul>
                <li>Human users (you)</li>
                <li>AI models with topologies</li>
                <li>3D spatial positioning</li>
                <li>Real-time state synchronization</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="welcome-section workflow">
          <h2>&gt; TYPICAL WORKFLOW</h2>
          <ol className="workflow-steps">
            <li>Create or browse existing simulations</li>
            <li>Configure AI models with topologies</li>
            <li>Join a simulation as an entity</li>
            <li>Interact via audio, movement, and other modalities</li>
            <li>Observe AI behavior and interactions</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
